/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useMemo, useState} from "react"
import {AddressElement, Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js"

import {formatCurrency, formatSavedPaymentMethodLabel, handleError, PAYMENT_METHOD_NEW} from "helpers"
import {studentPaymentService} from "services"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {
    PaymentProvider,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"
import {StudentCharge} from "types/student-account/student-ledger"
import {PaymentPlanItem} from "types/student-account/payment-plan"
import StripeElementsProvider from "providers/StripeElementsProvider"
import {BaseButton, KlassDropdown} from "components"
import {StudentPaymentContext} from "context/StudentPaymentContext"
import classNames from "classnames"
import {Checkbox, DatePicker, Radio, Space} from "antd"
import moment from "moment"
import styles from "../../PaymentModal.module.css"
import {FormLabel} from "components/Form"
import {useTranslation} from "react-i18next"
import {useStudentPaymentMethods} from "hooks"
import {routing} from "helpers/routing"
import {useHistory} from "react-router-dom"
import {round} from "lodash"

type Props = {
    providerConfig
    studentId: number
    paymentAmount: number
    currency: StudentPaymentSupportedCurrency
    isCheckingOut: boolean
    selectedPayment?: GeneralPayment
    customNote: string
    payerFullName?: string
    selectedCharges?: StudentCharge[]
    selectedPaymentItems?: PaymentPlanItem[]
    academicYear?: number
}

export const StripePayment = (props: Props) => {
    const {studentId, isCheckingOut} = props

    const [loadKey, setLoadKey] = useState(Math.random())

    useEffect(() => {
        setLoadKey(Math.random())
    }, [studentId, isCheckingOut])

    return (
        <StripeElementsProvider profileId={studentId} loadKey={loadKey}>
            <CheckoutForm {...props} />
        </StripeElementsProvider>
    )
}

const CheckoutForm = ({
    studentId,
    providerConfig,
    isCheckingOut,
    paymentAmount,
    currency,
    selectedPayment,
    customNote,
    payerFullName,
    academicYear,
    selectedCharges,
    selectedPaymentItems
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const history = useHistory()
    const stripe = useStripe()
    const elements = useElements()

    const [isPaymentRecurring, setPaymentRecurring] = useState(false)
    const [interval, setInterval] = useState<RecurringPaymentInterval>(RecurringPaymentInterval.Month)
    const [cancelAt, setCancelAt] = useState<moment.Moment | null>(null)
    const {paymentMethods, refetch: refetchPaymentMethods} = useStudentPaymentMethods({profileId: studentId})
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>()
    const {setPaymentStatus, setPaymentError} = useContext(StudentPaymentContext)

    // Auto-select the active payment method (or the first one) when methods load
    useEffect(() => {
        if (!paymentMethods.length) return
        setSelectedPaymentMethodId((prev) => {
            if (prev !== undefined) return prev
            const active = paymentMethods.find((m) => m.isActive) ?? paymentMethods[0]
            return active.paymentMethodId
        })
    }, [paymentMethods])

    const intervalOptions = useMemo(
        () =>
            Object.values(RecurringPaymentInterval).map((interval) => ({
                id: interval,
                name: t(`interval.${interval}`)
            })),
        [t]
    )

    const handleSubmit = React.useCallback(
        async (event) => {
            event.preventDefault()

            if (!stripe || !elements || !studentId) {
                return
            }

            if (!selectedPaymentMethodId) {
                handleError(new Error(t("msg.recurringPayment.paymentMethodRequired")))
                return
            }

            try {
                setPaymentStatus("processing")
                setPaymentError(undefined)

                let paymentMethodId = selectedPaymentMethodId

                if (selectedPaymentMethodId === PAYMENT_METHOD_NEW) {
                    const {error, setupIntent} = await stripe.confirmSetup({
                        elements,
                        redirect: "if_required",
                        confirmParams: {
                            return_url: window.location.origin
                        }
                    })
                    if (error) {
                        throw error
                    }
                    const paymentMethod = await studentPaymentService.stripeAttachPaymentMethod(
                        studentId,
                        setupIntent.payment_method as string
                    )
                    paymentMethodId = paymentMethod.paymentMethodId
                    refetchPaymentMethods()
                }

                if (!paymentMethodId) {
                    throw new Error(t("msg.recurringPayment.paymentMethodRequired"))
                }

                const amountInCents = Math.round((paymentAmount || 0) * 100)
                const generalPaymentId = selectedPayment?.generalPaymentId
                const paymentPlanItems = selectedPaymentItems?.length
                    ? selectedPaymentItems
                          .filter((item) => item.paymentAmount)
                          .map((item) => ({
                              paymentPlanId: item.paymentPlanId,
                              paymentPlanItemId: item.paymentPlanItemId,
                              accountId: item.paymentAccountId,
                              amount: paymentAmount
                          }))
                    : undefined
                const notes = `${selectedPayment?.name || ""}${customNote ? `: ${customNote}` : ""}`

                if (isPaymentRecurring) {
                    await studentPaymentService.createStudentRecurringPayment({
                        profileId: studentId,
                        provider: PaymentProvider.Stripe,
                        paymentMethodId,
                        amountInCents,
                        currency,
                        interval,
                        cancelAt: cancelAt?.toISOString() ?? null,
                        payerFullName,
                        academicYear,
                        generalPaymentId,
                        paymentPlanItems,
                        notes
                    })
                    // TODO post a message that recurring payment has been set up
                } else {
                    const {studentPayment, clientSecret, requiresAction} =
                        await studentPaymentService.createPaymentIntent({
                            amountInCents,
                            currency,
                            studentProfileId: studentId,
                            paymentMethodId,
                            payerFullName,
                            academicYear,
                            generalPaymentId,
                            paymentPlanItems,
                            notes
                        })

                    // Saved-card off-session charge requires 3D Secure authentication.
                    // confirmCardPayment handles both the auth challenge AND the confirmation
                    // (charge) in one call. handleCardAction would leave the intent in
                    // requires_confirmation with no follow-up confirm, so the webhook never fires.
                    if (requiresAction && clientSecret) {
                        const {error: actionError} = await stripe.confirmCardPayment(clientSecret)
                        if (actionError) {
                            // User cancelled or 3DS failed
                            throw actionError
                        }
                        // 3DS completed + payment confirmed — webhook will update DB to success/failed
                    }

                    if (selectedCharges?.length) {
                        await studentPaymentService.assignPaymentCharges(
                            studentPayment.paymentId,
                            selectedCharges.map((charge) => ({chargeId: charge.chargeId, amount: charge.paymentAmount}))
                        )
                    }
                }

                setPaymentStatus("confirmed")
            } catch (error: any) {
                console.error(error)
                setPaymentStatus("failed")
                // Stripe surfaces card declines and 3DS failures as typed errors.
                // Give users an actionable message instead of a generic one.
                if (error.type === "card_error" || error.type === "validation_error") {
                    const declineMessages: Record<string, string> = {
                        card_declined: t(
                            "error.cardDeclined",
                            "Your card was declined. Please try a different payment method."
                        ),
                        authentication_required: t(
                            "error.authenticationRequired",
                            "This payment requires authentication. Please complete the verification."
                        ),
                        insufficient_funds: t("error.insufficientFunds", "Your card has insufficient funds."),
                        expired_card: t("error.expiredCard", "Your card has expired."),
                        incorrect_cvc: t("error.incorrectCvc", "Your card's security code is incorrect."),
                        processing_error: t(
                            "error.processingError",
                            "An error occurred while processing your card. Please try again."
                        )
                    }
                    const message =
                        (error.code && declineMessages[error.code]) ||
                        error.message ||
                        t("error.cardGeneric", "Your card could not be charged.")
                    setPaymentError(message)
                } else {
                    setPaymentError(error.message || t("error.unexpected", "An unexpected error occurred."))
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            stripe,
            elements,
            isPaymentRecurring,
            interval,
            cancelAt,
            selectedPaymentMethodId,
            studentId,
            isCheckingOut,
            paymentAmount,
            currency,
            selectedPayment,
            customNote,
            payerFullName,
            selectedCharges,
            selectedPaymentItems
        ]
    )

    return (
        <form onSubmit={handleSubmit}>
            <div
                className={classNames(styles.checkbox, "mb-16", {
                    [styles.active]: isPaymentRecurring
                })}>
                <Checkbox checked={isPaymentRecurring} onChange={(event) => setPaymentRecurring(event.target.checked)}>
                    Make Payment Recurring
                </Checkbox>
            </div>

            {isPaymentRecurring && (
                <>
                    <div className="mb-24">
                        <FormLabel label={t("label.interval")} />
                        <KlassDropdown
                            isClearable={false}
                            options={intervalOptions}
                            value={intervalOptions.find((option) => option.id === interval)}
                            onChange={(option) => setInterval(option.id as RecurringPaymentInterval)}
                        />
                    </div>
                    <div className="mb-24">
                        <FormLabel label={t("label.endDate", "End Date (optional)")} />
                        <DatePicker
                            style={{width: "100%"}}
                            value={cancelAt}
                            onChange={setCancelAt}
                            disabledDate={(date) => date.isBefore(moment(), "day")}
                            allowClear
                        />
                    </div>
                </>
            )}

            <FormLabel label={t("label.paymentMethods")} />
            <Radio.Group
                className="mb-8"
                onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                value={selectedPaymentMethodId}>
                <Space direction="vertical">
                    {paymentMethods.map((method) => (
                        <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                            {formatSavedPaymentMethodLabel(method)}
                        </Radio>
                    ))}
                    <Radio value={PAYMENT_METHOD_NEW}>
                        {t("label.addNewPaymentMethod", "Add new card or bank account")}
                    </Radio>
                </Space>
            </Radio.Group>

            {selectedPaymentMethodId === PAYMENT_METHOD_NEW && (
                <>
                    <AddressElement options={{mode: "billing"}} />
                    <PaymentElement className="mt-24" />
                </>
            )}

            <BaseButton
                size="small"
                title="PAY"
                disabled={!stripe || !elements}
                htmlType="submit"
                className="w-full mt-24"
            />
        </form>
    )
}
